import React, { useEffect, useMemo, useState } from 'react';
import { notification } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import { exportToExcel } from '../../../Utils/ExcelExport';
import { getFacilityTemplateUsersByGod } from '../../../Apis/Facility';
import { ControlsDiV, SearchInputStyle, AddButton } from '../styledComponents';

const USER_TYPE_LABELS = {
  admin: 'Admin',
  doctor: 'Doctor',
  clinicAdmin: 'Clinic Admin',
  biller: 'Biller',
  reviewer: 'Reviewer',
  scribe: 'Scribe',
  transcriptionist: 'Transcriptionist',
  superadmin: 'Super Admin',
  cascadeGod: 'God'
};

const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const val = row.getValue(columnId);
  const strVal = val != null ? String(val) : 'N/A';
  return filterValue.includes(strVal);
};

const FacilityUsers = ({ templateId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);

  useEffect(() => {
    if (!templateId) return;
    setLoading(true);
    getFacilityTemplateUsersByGod(templateId)
      .then(response => {
        if (response.data.success) {
          setUsers(response.data.result || []);
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  }, [templateId]);

  const columns = useMemo(
    () => [
      {
        id: 'fullName',
        header: 'Name',
        accessorKey: 'fullName',
        enableSorting: true,
        sortingFn: 'text',
        filterFn: multiSelectFilter,
        cell: ({ getValue }) => getValue() || 'N/A'
      },
      {
        id: 'email',
        header: 'Email',
        accessorKey: 'email',
        enableSorting: true,
        filterFn: multiSelectFilter,
        cell: ({ getValue }) => getValue() || 'N/A'
      },
      {
        id: 'userType',
        header: 'User Type',
        accessorFn: row => USER_TYPE_LABELS[row.userType] || row.userType || 'N/A',
        enableSorting: true,
        filterFn: multiSelectFilter,
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'clinicAdminName',
        header: 'Clinic Admin',
        accessorKey: 'clinicAdminName',
        enableSorting: true,
        sortingFn: 'text',
        filterFn: multiSelectFilter,
        cell: ({ getValue }) => getValue() || 'N/A'
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: true,
        filterFn: multiSelectFilter,
        cell: ({ getValue }) => <span>{getValue() || '—'}</span>
      }
    ],
    []
  );

  const handleExport = () => {
    const term = searchString.trim().toLowerCase();
    const rows = users
      .map(user => ({
        fullName: user.fullName || '',
        email: user.email || '',
        userType: USER_TYPE_LABELS[user.userType] || user.userType || '',
        clinicAdminName: user.clinicAdminName || '',
        status: user.status || ''
      }))
      .filter(
        row =>
          !term ||
          row.fullName.toLowerCase().includes(term) ||
          row.email.toLowerCase().includes(term) ||
          row.userType.toLowerCase().includes(term) ||
          row.clinicAdminName.toLowerCase().includes(term) ||
          row.status.toLowerCase().includes(term)
      );

    const exportColumns = [
      { id: 'fullName', header: 'Name', accessorKey: 'fullName' },
      { id: 'email', header: 'Email', accessorKey: 'email' },
      { id: 'userType', header: 'User Type', accessorKey: 'userType' },
      { id: 'clinicAdminName', header: 'Clinic Admin', accessorKey: 'clinicAdminName' },
      { id: 'status', header: 'Status', accessorKey: 'status' }
    ];

    exportToExcel(rows, exportColumns, 'Facility Users');
  };

  return (
    <>
      <ControlsDiV style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
        <SearchInputStyle
          placeholder='Search'
          prefix={<SearchOutlined />}
          value={searchString}
          onChange={event => setSearchString(event.target.value)}
        />
        <AddButton icon={<DownloadOutlined />} onClick={handleExport}>
          Export to Excel
        </AddButton>
        <AddButton
          onClick={() => {
            setSearchString('');
            setClearFiltersTrigger(trigger => trigger + 1);
          }}
        >
          Clear Filter
        </AddButton>
      </ControlsDiV>
      <TanStackUserTable
        data={users}
        columns={columns}
        loading={loading}
        searchValue={searchString}
        storageKey='facility_users'
        clearFiltersTrigger={clearFiltersTrigger}
      />
    </>
  );
};

export default FacilityUsers;
